/* audit-rail.js — the Rewind Rail: a PURE view-model (segments/colors/breaks,
 * pin anchors, arm set) + a thin DOM renderer that consumes it. Two-layer
 * pattern (mirrors promotion-ladder.js). railViewModel/parseDiffLines have no
 * DOM dependency so the node:test suite imports them directly.
 */
import { esc, escAttr } from "./state.js";
import { iconHtml } from "./icons.js";

const _COLOR = { reversible: "success", risky: "warning", irreversible: "break" };

// A row is dimmed (struck, non-armable) when already reversed or itself a
// compensation (reverse_of set).
function _isDimmed(e) {
  return e.reverse_status === "reversed" || !!e.reverse_of;
}

export function railViewModel(groups, { armedEntryId = null, filtered = false } = {}) {
  const interactive = !filtered;
  const flat = [];
  const pins = [];
  for (const g of (groups || [])) {
    for (const c of (g.checkpoints || [])) {
      pins.push({ groupId: g.id, label: c.label, ts: c.ts != null ? c.ts : null, note: c.note || "" });
    }
    for (const e of (g.entries || [])) flat.push(e);
  }

  // Rewind-to-here undoes newest-through-armed over the canonical (newest-first)
  // order, excluding dimmed rows. Empty when non-interactive, nothing armed, or
  // the armed row is itself dimmed (not armable).
  const armSet = new Set();
  if (interactive && armedEntryId != null) {
    const armed = flat.find((e) => e.id === armedEntryId);
    if (armed && !_isDimmed(armed)) {
      for (const e of flat) {
        if (!_isDimmed(e)) armSet.add(e.id);
        if (e.id === armedEntryId) break;
      }
    }
  }

  const rows = flat.map((e) => {
    const dimmed = _isDimmed(e);
    return {
      id: e.id,
      color: _COLOR[e.reversibility] || "success",
      brk: e.reversibility === "irreversible",
      dimmed,
      armable: interactive && !dimmed,
      armed: armSet.has(e.id),
    };
  });

  return { interactive, rows, pins, armSet };
}

export function parseDiffLines(diffStr) {
  const s = String(diffStr == null ? "" : diffStr);
  if (!s) return [];
  const lines = s.split("\n");
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  return lines.map((line) => {
    let cls;
    if (line.startsWith("+++") || line.startsWith("---") || line.startsWith("@@")) cls = "d-hunk";
    else if (line.startsWith("+")) cls = "d-add";
    else if (line.startsWith("-")) cls = "d-del";
    else cls = "d-ctx";
    return { cls, text: line };
  });
}

// ---- DOM layer (consumes railViewModel; not exercised by node:test) --------

// The rail cell rendered at the left of each entry row. `railRow` is one element
// of railViewModel().rows. Attribute-driven so an arm re-render is a cheap swap.
export function railCellHtml(railRow) {
  const cls = ["v2a-rail", "v2a-rail-" + railRow.color,
    railRow.brk ? "v2a-brk" : "", railRow.dimmed ? "v2a-dim" : "",
    railRow.armed ? "v2a-armed" : ""].filter(Boolean).join(" ");
  const node = railRow.brk
    ? iconHtml("git-commit", { size: "sm" })
    : `<span class="v2a-node" aria-hidden="true"></span>`;
  return `<span class="${cls}">${node}</span>`;
}

export function pinRowHtml(pin) {
  const when = pin.ts ? ` · ${esc(String(pin.ts).slice(0, 16).replace("T", " "))}` : "";
  const note = pin.note ? ` · ${esc(pin.note)}` : "";
  return `<div class="v2a-pinrow" data-checkpoint="${escAttr(pin.label)}">`
    + `${iconHtml("pin", { size: "sm" })}<span class="v2a-pin-label">${esc(pin.label)}</span>`
    + `<span class="v2a-pin-meta">${when}${note}</span></div>`;
}
